/**
 * v1 → v2 archetype bridge.
 *
 * The v1 instrument scores eight "Career DNA" archetypes:
 *   Builder · Thinker · Advocate · Caregiver · Creator · Organizer · Leader · Adventurer
 *
 * v2's canonical eight are:
 *   Builder · Thinker · Creator · Connector · Organizer · Leader · Caregiver · Advocate
 *
 * Seven names are shared. The one divergence is v1's **Adventurer**, which has
 * no v2 row, and v2's **Connector**, which has no v1 row.
 *
 * THE REDISTRIBUTION RULE (the only thing in this port that is not verbatim v1):
 *   Adventurer folds into **Builder** — hands-on, physical, out-in-the-world
 *   work. This is the same fold `passions.ts` already applies to the v1 passion
 *   set, and one instrument must not disagree with another about what a v1
 *   archetype means.
 *   Nothing is folded into Connector. Connector is a social-brokerage
 *   archetype and v1 has no item measuring it, so inventing a Connector score
 *   from Adventurer evidence would be a fabricated signal. Connector therefore
 *   receives 0 from this instrument and is scored only from v2's own screens.
 *
 * CANON-PENDING: the Adventurer fold belongs in the Command Center item bank.
 * It is recorded here until it is sealed there.
 *
 * Everything below this line is copied from v1 verbatim.
 */

import { ARCHETYPES, emptyVector, type Archetype, type ArchetypeVector } from "../archetypes";

/** The eight v1 archetypes, in v1's order. */
export const V1_ARCHETYPES = [
  "Builder",
  "Thinker",
  "Advocate",
  "Caregiver",
  "Creator",
  "Organizer",
  "Leader",
  "Adventurer",
] as const;

export type V1Archetype = (typeof V1_ARCHETYPES)[number];

/** Map WeKIT™ traits → Career DNA archetypes (weighted). Verbatim from v1. */
export const TRAIT_TO_ARCHETYPE: Record<string, { archetype: V1Archetype; weight: number }[]> = {
  "Self-awareness":        [{ archetype: "Thinker", weight: 1 }],
  "Risk Orientation":      [{ archetype: "Adventurer", weight: 1 }],
  "Decision Making":       [{ archetype: "Thinker", weight: 0.6 }, { archetype: "Leader", weight: 0.4 }],
  "Communication":         [{ archetype: "Leader", weight: 0.5 }, { archetype: "Advocate", weight: 0.5 }],
  "Empathy":               [{ archetype: "Caregiver", weight: 0.6 }, { archetype: "Advocate", weight: 0.4 }],
  "Work Style":            [{ archetype: "Creator", weight: 0.5 }, { archetype: "Adventurer", weight: 0.5 }],
  "Goal Orientation":      [{ archetype: "Organizer", weight: 0.5 }, { archetype: "Leader", weight: 0.5 }],
  "Planning":              [{ archetype: "Organizer", weight: 1 }],
  "Social Initiative":     [{ archetype: "Leader", weight: 0.6 }, { archetype: "Adventurer", weight: 0.4 }],
  "Practical Skills":      [{ archetype: "Builder", weight: 1 }],
  "Resilience":            [{ archetype: "Adventurer", weight: 0.5 }, { archetype: "Builder", weight: 0.5 }],
  "Emotional Regulation":  [{ archetype: "Caregiver", weight: 1 }],
  "Creativity":            [{ archetype: "Creator", weight: 1 }],
  "Collaboration":         [{ archetype: "Caregiver", weight: 0.5 }, { archetype: "Organizer", weight: 0.5 }],
  "Digital Literacy":      [{ archetype: "Builder", weight: 0.5 }, { archetype: "Thinker", weight: 0.5 }],
  "Leadership":            [{ archetype: "Leader", weight: 1 }],
  "Knowledge Sharing":     [{ archetype: "Advocate", weight: 0.6 }, { archetype: "Caregiver", weight: 0.4 }],
  "Adaptability":          [{ archetype: "Adventurer", weight: 0.6 }, { archetype: "Creator", weight: 0.4 }],
  "Ethical Reasoning":     [{ archetype: "Advocate", weight: 1 }],
  "Entrepreneurial Spirit":[{ archetype: "Adventurer", weight: 0.4 }, { archetype: "Leader", weight: 0.3 }, { archetype: "Creator", weight: 0.3 }],
  "Critical Thinking":     [{ archetype: "Thinker", weight: 1 }],
  "Well-being Management": [{ archetype: "Caregiver", weight: 1 }],
  "Inclusivity":           [{ archetype: "Advocate", weight: 0.5 }, { archetype: "Caregiver", weight: 0.5 }],
  "Future Tech Orientation":[{ archetype: "Thinker", weight: 0.5 }, { archetype: "Builder", weight: 0.5 }],
  "Problem-Solving":       [{ archetype: "Thinker", weight: 0.6 }, { archetype: "Builder", weight: 0.4 }],
  "Social Responsibility": [{ archetype: "Advocate", weight: 1 }],
};

/** Superpower descriptions for traits. Verbatim from v1. */
export const TRAIT_SUPERPOWERS: Record<string, string> = {
  "Self-awareness": "You deeply understand your own strengths and growth areas — a rare superpower.",
  "Risk Orientation": "You thrive on bold moves and adventurous pursuits.",
  "Decision Making": "You make sharp, logical decisions under pressure.",
  "Communication": "You're a natural explainer who makes complex ideas simple.",
  "Empathy": "People trust you with their problems — you feel what others feel.",
  "Work Style": "You thrive in dynamic, ever-changing environments.",
  "Goal Orientation": "You set targets and relentlessly pursue them.",
  "Planning": "You turn chaos into structure with ease.",
  "Social Initiative": "You walk into any room and build connections instantly.",
  "Practical Skills": "You solve real-world problems with your hands and mind.",
  "Resilience": "You bounce back stronger from every setback.",
  "Emotional Regulation": "You stay calm and composed when others can't.",
  "Creativity": "You see possibilities where others see problems.",
  "Collaboration": "You bring out the best in every team you join.",
  "Digital Literacy": "You navigate technology like a native — future-ready.",
  "Leadership": "People naturally look to you for direction and inspiration.",
  "Knowledge Sharing": "You elevate everyone around you by sharing what you know.",
  "Adaptability": "You thrive in change — it's your element.",
  "Ethical Reasoning": "You stand for what's right, even when it's hard.",
  "Entrepreneurial Spirit": "You spot opportunities and create value from nothing.",
  "Critical Thinking": "You cut through noise to find the truth.",
  "Well-being Management": "You balance ambition with self-care — sustainable success.",
  "Inclusivity": "You build bridges across differences and bring people together.",
  "Future Tech Orientation": "You anticipate tomorrow's tech and prepare today.",
  "Problem-Solving": "You untangle complexity with analytical precision.",
  "Social Responsibility": "You're driven to make the world better for everyone.",
};

/** Archetype → strength description, for explainability. Verbatim from v1. */
export const V1_ARCHETYPE_STRENGTHS: Record<V1Archetype, string> = {
  Builder: "hands-on problem-solving and technical skills",
  Thinker: "analytical thinking and logical reasoning",
  Advocate: "passion for social impact and ethical values",
  Caregiver: "empathy, emotional intelligence, and people-first mindset",
  Creator: "creativity, imagination, and innovative thinking",
  Organizer: "structured planning, discipline, and goal orientation",
  Leader: "natural leadership, communication, and initiative",
  Adventurer: "adaptability, risk-taking, and entrepreneurial spirit",
};

/**
 * v1 archetype name → v2 canonical archetype id, with the split weights.
 * Adventurer is the only 1-to-many row; see the redistribution rule above.
 */
export const V1_TO_V2: Record<V1Archetype, Partial<Record<Archetype, number>>> = {
  Builder: { Builder: 1 },
  Thinker: { Thinker: 1 },
  Advocate: { Advocate: 1 },
  Caregiver: { Caregiver: 1 },
  Creator: { Creator: 1 },
  Organizer: { Organizer: 1 },
  Leader: { Leader: 1 },
  Adventurer: { Builder: 1 },
};

/**
 * Project a v1 archetype vector (0–100 each) onto v2's canonical eight.
 * Connector always comes back as 0 — this instrument does not measure it.
 */
export function toV2Archetypes(
  v1: Partial<Record<V1Archetype, number>>,
): ArchetypeVector {
  const out = emptyVector();
  for (const name of V1_ARCHETYPES) {
    const value = v1[name] ?? 0;
    if (value <= 0) continue;
    for (const [id, share] of Object.entries(V1_TO_V2[name])) {
      out[id as Archetype] += value * (share ?? 0);
    }
  }
  for (const id of ARCHETYPES) {
    out[id] = Math.max(0, Math.min(100, Math.round(out[id])));
  }
  return out;
}
